import type {
  HttpClientOfflineCacheAdapter,
  HttpClientOfflineCacheEntry,
  HttpClientOfflineCacheReadResult,
  HttpClientOfflineCacheResolvedScope,
} from '@tov-plus/sdk';

export type OfflineCacheApp = HttpClientOfflineCacheResolvedScope['app'];
export type OfflineCacheEntry<T> = HttpClientOfflineCacheEntry<T>;

const databaseName = 'tov-plus-offline-data';
const storeName = 'offline_entries';
const cacheNamespace = 'offline-data-v1';
const schemaVersion = 1;
const defaultMaxEntries = 160;
const defaultMaxAgeMs = 7 * 24 * 60 * 60 * 1_000;

type StoredEntry<T = unknown> = HttpClientOfflineCacheEntry<T> & {
  namespace: string;
  id: string;
};

export interface OfflineDataCacheOptions {
  maxEntries?: number;
  maxAgeMs?: number;
}

function isIndexedDbAvailable() {
  return typeof indexedDB !== 'undefined';
}

function buildEntryId(key: string, scope: HttpClientOfflineCacheResolvedScope) {
  return [
    cacheNamespace,
    scope.app,
    scope.occasionId ?? '',
    scope.actorId ?? '',
    scope.userId ?? '',
    scope.guestTokenHash ?? '',
    key,
  ]
    .map((part) => encodeURIComponent(part))
    .join('|');
}

function requestToPromise<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

function transactionDone(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();
  });
}

async function openDatabase() {
  if (!isIndexedDbAvailable()) {
    return null;
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, schemaVersion);

    request.onerror = () => reject(request.error);
    request.onupgradeneeded = () => {
      const database = request.result;

      if (database.objectStoreNames.contains(storeName)) {
        database.deleteObjectStore(storeName);
      }

      const store = database.createObjectStore(storeName, { keyPath: 'id' });
      store.createIndex('namespace', 'namespace');
      store.createIndex('app', 'app');
      store.createIndex('savedAt', 'savedAt');
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function scopeMatches(
  entry: StoredEntry,
  scope: Partial<HttpClientOfflineCacheResolvedScope>,
) {
  return (
    (!scope.app || entry.app === scope.app) &&
    (!scope.occasionId || entry.occasionId === scope.occasionId) &&
    (!scope.actorId || entry.actorId === scope.actorId) &&
    (!scope.userId || entry.userId === scope.userId) &&
    (!scope.guestTokenHash || entry.guestTokenHash === scope.guestTokenHash)
  );
}

function isEntryExpired(entry: StoredEntry, maxAgeMs: number) {
  return Date.now() - new Date(entry.savedAt).getTime() > maxAgeMs;
}

function isEntryStale(entry: StoredEntry) {
  return entry.staleAfter
    ? Date.now() > new Date(entry.staleAfter).getTime()
    : false;
}

export function createOfflineDataCache(
  options: OfflineDataCacheOptions = {},
): HttpClientOfflineCacheAdapter {
  const maxEntries = options.maxEntries ?? defaultMaxEntries;
  const maxAgeMs = options.maxAgeMs ?? defaultMaxAgeMs;

  async function prune(database: IDBDatabase) {
    const readTransaction = database.transaction(storeName, 'readonly');
    const entries = (await requestToPromise(
      readTransaction.objectStore(storeName).getAll(),
    )) as StoredEntry[];
    await transactionDone(readTransaction);

    const currentNamespaceEntries = entries
      .filter((entry) => entry.namespace === cacheNamespace)
      .sort(
        (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime(),
      );
    const expiredOrOldEntries = entries.filter(
      (entry) =>
        entry.namespace !== cacheNamespace ||
        isEntryExpired(entry, maxAgeMs) ||
        currentNamespaceEntries.indexOf(entry) >= maxEntries,
    );

    if (expiredOrOldEntries.length === 0) {
      return;
    }

    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    for (const entry of expiredOrOldEntries) {
      store.delete(entry.id);
    }

    await transactionDone(transaction);
  }

  return {
    async read<T>(
      key: string,
      scope: HttpClientOfflineCacheResolvedScope,
    ): Promise<HttpClientOfflineCacheReadResult<T> | null> {
      const database = await openDatabase();

      if (!database) {
        return null;
      }

      try {
        const transaction = database.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const entry = (await requestToPromise(
          store.get(buildEntryId(key, scope)),
        )) as StoredEntry<T> | undefined;

        if (
          !entry ||
          entry.namespace !== cacheNamespace ||
          entry.schemaVersion !== schemaVersion ||
          isEntryExpired(entry, maxAgeMs)
        ) {
          return null;
        }

        return {
          entry,
          isStale: isEntryStale(entry),
        };
      } finally {
        database.close();
      }
    },

    async write<T>(
      entry: Omit<HttpClientOfflineCacheEntry<T>, 'savedAt' | 'schemaVersion'>,
    ) {
      const database = await openDatabase();

      if (!database) {
        return;
      }

      try {
        const savedAt = new Date().toISOString();
        const storedEntry: StoredEntry<T> = {
          ...entry,
          id: buildEntryId(entry.key, entry),
          namespace: cacheNamespace,
          savedAt,
          schemaVersion,
        };
        const transaction = database.transaction(storeName, 'readwrite');
        transaction.objectStore(storeName).put(storedEntry);
        await transactionDone(transaction);
        await prune(database);
      } finally {
        database.close();
      }
    },

    async clearScope(scope: Partial<HttpClientOfflineCacheResolvedScope>) {
      const database = await openDatabase();

      if (!database) {
        return;
      }

      try {
        const transaction = database.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const entries = (await requestToPromise(
          store.getAll(),
        )) as StoredEntry[];

        for (const entry of entries) {
          if (
            entry.namespace === cacheNamespace &&
            scopeMatches(entry, scope)
          ) {
            store.delete(entry.id);
          }
        }

        await transactionDone(transaction);
      } finally {
        database.close();
      }
    },
  };
}
